import {
  AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy,
  ViewChild, computed, effect, inject, signal,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BrandMarkComponent } from '../brand-mark/brand-mark.component';
import { TournamentDataService } from '../../../core/services/tournament-data.service';
import { AuthService } from '../../../core/services/auth.service';
import { HeroLogoService } from '../../../core/services/hero-logo.service';
import { ThemeService } from '../../../core/services/theme.service';
import { VisualViewportDirective } from '../../directives/visual-viewport.directive';

/**
 * Floor for the flight distance. The real distance is measured (see `flight`),
 * but a hero shorter than this would otherwise snap the logo into the nav in a
 * few pixels of scroll.
 */
const TRAVEL_MIN = 320;

/**
 * Fraction of the flight the logo spends sitting perfectly still on the hero.
 *
 * Without it the logo lifts away the instant the page moves, and it is already
 * parked in the header while most of the hero is still on screen — leaving a
 * conspicuous gap where it used to be. Holding it for the first third means it
 * only leaves once the hero itself is genuinely on its way out.
 */
const HOLD = 0.34;

@Component({
  selector: 'app-nav-bar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, BrandMarkComponent, VisualViewportDirective],
  templateUrl: './nav-bar.component.html',
  styleUrl: './nav-bar.component.scss',
})
export class NavBarComponent implements AfterViewInit, OnDestroy {
  private readonly data = inject(TournamentDataService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly heroLogo = inject(HeroLogoService);
  private readonly themeService = inject(ThemeService);
  private readonly zone = inject(NgZone);

  @ViewChild('brandSlot') private brandRef?: ElementRef<HTMLElement>;
  @ViewChild('flyLogo') private logoRef?: ElementRef<HTMLImageElement>;

  readonly menuOpen = signal(false);
  readonly condensed = signal(false);

  /** Falls back to the typographic lockup if `logo.png` is ever missing. */
  readonly logoBroken = signal(false);

  /** Drives the count bubble on the Live link — the nav's one live signal. */
  readonly liveCount = computed(() => this.data.liveMatches().length);
  readonly isStaff = this.auth.isLoggedIn;

  readonly isLight = this.themeService.isLight;
  /** The travelling logo needs the blue cut on a pale ground. */
  readonly logoSrc = this.themeService.logoSrc;

  /** Registration runs on the Sprentzo site; the nav's call to action opens it in a new tab. */
  readonly registerUrl = 'https://www.sprentzo.com/pages/kalaburagi-sports-habba-registration';

  readonly links = [
    { label: 'Home', path: '/' },
    { label: 'Matches', path: '/matches' },
    { label: 'Live', path: '/live' },
    { label: 'Gallery', path: '/gallery' },
  ];

  private frame = 0;
  private running = false;
  private reduced = false;
  private lastProgress = -1;
  private cleanups: (() => void)[] = [];

  constructor() {
    // A route change must always close the drawer, however it was triggered —
    // link tap, back button, or a redirect.
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this.closeMenu());

    // Leaving the home page removes the anchor, so the logo docks; arriving
    // back re-registers it and the loop picks up again.
    effect(() => {
      this.heroLogo.anchor();
      if (!this.logoRef) return;
      this.lastProgress = -1;
      queueMicrotask(() => (this.reduced ? this.applyStatic() : this.start()));
    });
  }

  ngAfterViewInit(): void {
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // The logo is laid out at *hero* size and scaled down to dock. Scaling a
    // raster down always stays sharp; scaling one up does not — which is why
    // the geometry runs this way round rather than the intuitive one.
    this.zone.runOutsideAngular(() => {
      if (this.reduced) {
        this.applyStatic();
        const onScroll = () => this.applyStatic();
        const onResize = () => this.applyStatic();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize, { passive: true });
        this.cleanups.push(
          () => window.removeEventListener('scroll', onScroll),
          () => window.removeEventListener('resize', onResize),
        );
        return;
      }

      const onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', onVisibility);
      this.cleanups.push(() => document.removeEventListener('visibilitychange', onVisibility));
      this.start();
    });
  }

  ngOnDestroy(): void {
    this.stop();
    this.cleanups.forEach(fn => fn());
    this.cleanups = [];
  }

  // ---- the flight ---------------------------------------------------------

  private start(): void {
    if (this.running || this.reduced) return;
    this.running = true;
    this.frame = requestAnimationFrame(this.tick);
  }

  private stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frame);
  }

  private readonly tick = (): void => {
    if (!this.running) return;
    this.place();
    this.frame = requestAnimationFrame(this.tick);
  };

  private place(): void {
    const logo = this.logoRef?.nativeElement;
    const brand = this.brandRef?.nativeElement;
    if (!logo || !brand) return;

    const anchor = this.heroLogo.anchor();

    // Already parked and nothing can have moved — skip the layout reads.
    if (!anchor && this.lastProgress === 1) return;

    // Read before writing: the rects are measured at the top of the frame, so
    // last frame's transform is already committed and nothing forces a
    // synchronous re-layout.
    const brandBox = brand.getBoundingClientRect();
    const dock = brandBox.width / (logo.offsetWidth || 1);

    if (!anchor) {
      this.lastProgress = 1;
      logo.style.transform = `translate3d(0,0,0) scale(${dock})`;
      logo.classList.add('nav__logo--ready');
      return;
    }

    const slot = anchor.getBoundingClientRect();
    const progress = Math.min(1, Math.max(0, window.scrollY / this.flight(slot, brandBox)));

    if (progress === 1 && this.lastProgress === 1) return;
    this.lastProgress = progress;

    // Nothing happens at all for the first `HOLD` of the flight: the logo is
    // pinned to its place in the hero and scrolls with the page like any other
    // element, so the hero stays whole. `eased` is the remaining travel,
    // re-based to 0..1, which is why the logo does not jump when it releases.
    const eased = Math.max(0, (progress - HOLD) / (1 - HOLD));

    // Two curves, deliberately different. Size settles early, so the logo is
    // already nav-sized by the time it crosses the header band and can never
    // collide with the links; position eases in and out, so the travel itself
    // reads as one continuous movement rather than a slide.
    const shrink = 1 - Math.pow(1 - eased, 3);
    const glide = eased * eased * (3 - 2 * eased);

    const scale = 1 + (dock - 1) * shrink;
    const dx = (slot.left - brandBox.left) * (1 - glide);
    // Never above the dock, whatever the easing does in between: the logo
    // settles into the header, it does not fly out through the top of it.
    const dy = Math.max(0, (slot.top - brandBox.top) * (1 - glide));

    logo.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(${scale})`;
    logo.classList.add('nav__logo--ready');
  }

  /**
   * How far the page must scroll for the logo to complete its flight: far
   * enough that the hero slot has carried its whole height up past the header.
   * Measured rather than chosen, so it needs no tuning per breakpoint — a
   * short hero on a phone gets a proportionally shorter flight.
   */
  private flight(slot: DOMRect, brandBox: DOMRect): number {
    const slotBottomDoc = slot.top + window.scrollY + slot.height;
    return Math.max(TRAVEL_MIN, slotBottomDoc - (brandBox.top + brandBox.height));
  }

  /** Reduced motion: no travel, no easing — the logo is in one place or the other. */
  private applyStatic(): void {
    const logo = this.logoRef?.nativeElement;
    const brand = this.brandRef?.nativeElement;
    if (!logo || !brand) return;

    const anchor = this.heroLogo.anchor();
    const brandBox = brand.getBoundingClientRect();
    const dock = brandBox.width / (logo.offsetWidth || 1);

    const slot = anchor?.getBoundingClientRect();
    const threshold = slot ? this.flight(slot, brandBox) * 0.6 : 0;

    if (slot && window.scrollY < threshold) {
      logo.style.transform =
        `translate3d(${slot.left - brandBox.left}px, ${slot.top - brandBox.top}px, 0) scale(1)`;
    } else {
      logo.style.transform = `translate3d(0,0,0) scale(${dock})`;
    }
    logo.classList.add('nav__logo--ready');
  }

  // ---- nav chrome ---------------------------------------------------------

  @HostListener('window:scroll')
  onScroll(): void {
    this.condensed.set(window.scrollY > 20);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeMenu();
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  toggleMenu(): void {
    this.menuOpen.update(v => !v);
    // Lock the page behind an open drawer so the backdrop doesn't scroll.
    document.body.style.overflow = this.menuOpen() ? 'hidden' : '';
  }

  closeMenu(): void {
    if (!this.menuOpen()) return;
    this.menuOpen.set(false);
    document.body.style.overflow = '';
  }
}
